import React, { useState, useEffect } from 'react';
import { Link, navigate } from 'gatsby'
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight } from 'lucide-react';
import icon from '../images/icon-nav.png';

const ALLOWED_ENTRY_PATHS = [
  '/iso-9001',
  '/directive-nis2',
  '/iso-27001',
  '/data-product-passport',
  '/norme-nf-z60-200'
];

const MENU_ITEMS = [
  { path: '/', label: "Cas d'usage" },
  { path: '/solution', label: "Solution" },
  { path: '/technologie', label: 'Technologie' },
  { path: '/contact', label: 'Contact' }
];

const normalizePath = (path) => {
  if (!path) return path;
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
};

const getEntryPage = () => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage?.getItem('baasEntryPage') || null;
  } catch {
    return null;
  }
};

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [entryPage, setEntryPage] = useState(null);
  const hasEntryPage = Boolean(entryPage);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const pathname = normalizePath(window.location.pathname);

    try {
      if (ALLOWED_ENTRY_PATHS.includes(pathname)) {
        window.localStorage.setItem('baasEntryPage', pathname);
      } else {
        window.localStorage.removeItem('baasEntryPage');
      }
    } catch (e) {}

    setEntryPage(getEntryPage());
  }, []);

  const handleLogoClick = (event) => {
    if (typeof window === 'undefined') {
      return;
    }

    event.preventDefault();
    if (entryPage) {
      navigate(entryPage);
    }
  };

  const filteredMenuItems = hasEntryPage
    ? MENU_ITEMS
    : MENU_ITEMS.filter(item => item.path !== '/');

  const containerVariants = {
    open: { 
      opacity: 1,
      height: "auto",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
        staggerChildren: 0.1
      }
    },
    closed: { 
      opacity: 0,
      height: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const itemVariants = {
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    closed: {
      opacity: 0,
      x: -20,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  return (
    <header className="site-header backdrop-blur-sm fixed w-full z-50">
      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        <Link 
          to="/" 
          className="flex items-center space-x-2"
          onClick={handleLogoClick}
        >
          <img
            src={icon}
            alt="BaaS"
            className="h-10 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center space-x-8">
          {filteredMenuItems.map(item => (
            <Link
              key={item.path}
              to={item.path === '/' && entryPage ? entryPage : item.path}
              className="nav-link text-sm font-medium"
              activeClassName="nav-link-active"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2 rounded-lg transition-opacity hover:opacity-70"
          aria-label="Ouvrir ou fermer le menu"
          style={{color: 'var(--text-primary)'}}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={isOpen ? 'close' : 'menu'}
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.2 }}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.div>
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden border-t"
            style={{ borderColor: 'var(--border-subtle)' }}
            initial="closed"
            animate="open"
            exit="closed"
            variants={containerVariants}
          >
            <div className="px-4 py-4 space-y-4">
              {filteredMenuItems.map((item, index) => (
                <motion.div key={item.path} variants={itemVariants}>
                  <Link
                    to={item.path === '/' && entryPage ? entryPage : item.path}
                    className="flex items-center justify-between p-4 text-lg font-medium transition-colors rounded-lg group"
                    activeClassName="mobile-link-active"
                    style={{ 
                      color: 'var(--text-primary)',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--accent)';
                      e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--text-primary)';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => setIsOpen(false)}
                  >
                    <span>{item.label}</span>
                    <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-all duration-200" style={{ color: 'var(--accent)' }} />
                  </Link>
                  {index < filteredMenuItems.length - 1 && (
                    <div className="h-px my-2" style={{ backgroundColor: 'var(--border-subtle)' }} />
                  )}
                </motion.div>
              ))}
              {/* Uncomment if you want to add the Get Started button
              <motion.div variants={itemVariants}>
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2">
                  <span>Get Started</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </motion.div>
              */}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;